'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { Icon } from '@/components/ui/icon';
import { Loading03Icon } from '@/components/ui/icon/glyphs';
import { Link } from '@/i18n/navigation';
import { useAppRouter } from '@/lib/network/use-app-router';
import { buildTradeCanonicalUrl } from '@/lib/share';

interface TradeRedirectProps {
  marketPubkey: string;
}

const ESCAPE_HATCH_DELAY_MS = 10_000;

/**
 * Tiny client island that bounces a `/trade/<sig>?...` visit to the
 * canonical market URL. Mirrors the thesis-share redirect so share-link
 * pages feel cohesive across surfaces.
 *
 * `router.replace` (not `push`) keeps the share URL out of the back
 * button so a back-tap from the market page lands on the previous tab.
 */
export function TradeRedirect({ marketPubkey }: TradeRedirectProps): React.ReactNode {
  const router = useAppRouter();
  const t = useTranslations('trade.share');
  const target = buildTradeCanonicalUrl(marketPubkey);
  const [showEscape, setShowEscape] = useState(false);

  useEffect((): (() => void) => {
    router.replace(target);
    const timer = setTimeout((): void => {
      setShowEscape(true);
    }, ESCAPE_HATCH_DELAY_MS);
    return (): void => {
      clearTimeout(timer);
    };
  }, [router, target]);

  return (
    <main className="flex min-h-dvh flex-col items-center justify-center gap-4">
      <Icon
        icon={Loading03Icon}
        className="size-10 animate-spin text-muted-foreground motion-reduce:animate-none"
      />
      <p className="text-body text-muted-foreground">{t('redirecting')}</p>
      {showEscape && (
        <p className="animate-in fade-in-0 duration-slow text-sm text-muted-foreground">
          {t('taking_too_long')}{' '}
          <Link href="/" className="underline">
            {t('go_home')}
          </Link>
        </p>
      )}
    </main>
  );
}
